import { Menu } from 'components/Menu/Menu'
import { Section } from '@react-stately/collections'
import { Strikethrough, Underline } from 'blaze-ui/icons'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { useStrikethroughToggle } from 'common/Prose/Toolbar/hooks/useStrikethroughToggle'
import { observer } from 'mobx-react-lite'
import { Key, useMemo } from 'react'
import { MenuProps } from 'components/Menu/Types'
import { useUnderlineToggle } from 'common/Prose/Toolbar/hooks/useUnderlineToggle'
import { InlineCode } from 'almanac-ui/icons'
import { useInlineCodeToggle } from 'common/Prose/Toolbar/hooks/useInlineCodeToggle'
import { useSuperscriptToggle } from 'common/Prose/Toolbar/hooks/useSuperscriptToggle'
import { ReactComponent as SuperscriptIcon } from 'assets/icons/superscript16.svg'
import { useSubscriptToggle } from 'common/Prose/Toolbar/hooks/useSubscriptToggle'
import { ReactComponent as SubscriptIcon } from 'assets/icons/subscript16.svg'
import { useClearFormattingButton } from 'common/Prose/Toolbar/hooks/useClearFormattingButton'
import { Item } from 'components/Menu/MenuItem'

export const MoreOptionsMenu = observer(({ onAction }: Pick<MenuProps<any>, 'onAction'>) => {
  const view = useEditorView()
  const {
    active: isStrikeThroughActive,
    disabled: isStrikeThroughDisabled,
    handleClick: handleStrikethroughToggle,
  } = useStrikethroughToggle(view)
  const {
    active: isUnderlineActive,
    disabled: isUnderlineDisabled,
    handleClick: handleUnderlineToggle,
  } = useUnderlineToggle(view)
  const {
    active: isInlineCodeActive,
    disabled: isInlineCodeDisabled,
    handleClick: handleInlineCodeToggle,
  } = useInlineCodeToggle(view)
  const {
    active: isSuperscriptActive,
    disabled: isSuperscriptDisabled,
    handleClick: handleSuperscriptToggle,
  } = useSuperscriptToggle(view)
  const {
    active: isSubscriptActive,
    disabled: isSubscriptDisabled,
    handleClick: handleSubscriptToggle,
  } = useSubscriptToggle(view)
  const clearFormatting = useClearFormattingButton(view)

  const disabledKeys = useMemo(() => {
    const disabledKeys = new Set<Key>()
    isStrikeThroughDisabled && disabledKeys.add('strikeThrough')
    isUnderlineDisabled && disabledKeys.add('underline')
    isInlineCodeDisabled && disabledKeys.add('inlineCode')
    isSuperscriptDisabled && disabledKeys.add('superscript')
    isSubscriptDisabled && disabledKeys.add('subscript')
    return disabledKeys
  }, [isStrikeThroughDisabled, isUnderlineDisabled, isInlineCodeDisabled, isSuperscriptDisabled, isSubscriptDisabled])

  const selectedKeys = useMemo(() => {
    const selectedKeys = new Set<Key>()
    isStrikeThroughActive && selectedKeys.add('strikeThrough')
    isUnderlineActive && selectedKeys.add('underline')
    isInlineCodeActive && selectedKeys.add('inlineCode')
    isSuperscriptActive && selectedKeys.add('superscript')
    isSubscriptActive && selectedKeys.add('subscript')
    return selectedKeys
  }, [isStrikeThroughActive, isUnderlineActive, isInlineCodeActive, isSuperscriptActive, isSubscriptActive])

  const handleAction = (action: Key) => {
    onAction?.(action)
    switch (action) {
      case 'strikeThrough':
        return handleStrikethroughToggle()
      case 'underline':
        return handleUnderlineToggle()
      case 'inlineCode':
        return handleInlineCodeToggle()
      case 'superscript':
        return handleSuperscriptToggle()
      case 'subscript':
        return handleSubscriptToggle()
      case 'clearFormatting':
        return clearFormatting()
    }
  }

  return (
    <Menu
      menuType="tray"
      selectionMode="multiple"
      selectedKeys={selectedKeys}
      disabledKeys={disabledKeys}
      onAction={handleAction}
    >
      <Section>
        <Item key="strikeThrough" textValue="Strike Through" frontIcon={Strikethrough}>
          Strike Through
        </Item>
        <Item key="underline" textValue="Underline" frontIcon={Underline}>
          Underline
        </Item>
        <Item key="inlineCode" textValue="Inline Code" frontIcon={InlineCode}>
          Inline Code
        </Item>
        <Item key="superscript" textValue="Superscript" frontIcon={SuperscriptIcon}>
          Superscript
        </Item>
        <Item key="subscript" textValue="Subscript" frontIcon={SubscriptIcon}>
          Subscript
        </Item>
      </Section>
    </Menu>
  )
})
